import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import { useStateContext } from '../../../contexts/ContextProvider';
import type { Message } from '../../../pages/cardex/api/useMessage';

import BulleMessage from '../../components/BulleMessage';
import Composeur from '../../components/Composeur';
import CarteContexte from '../../components/CarteContexte';
import CorpsEmail from '../../components/CorpsEmail';
import EtatErreur from '../../components/EtatErreur';
import IconButton from '../../components/IconButton';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import { estAutomatique, useFilConversation } from '../../api/useFilConversation';
import { estFermee, useFermerConversation, useIdDossierFerme } from '../../api/useDossierFerme';
import { useMarquerNonLu } from '../../api/useMarquerNonLu';
import { libelleJour, nomComplet, telHref } from '../../helpers/conversation';
import { echecSansDonnee } from '../../helpers/requete';
import { t } from '../../i18n/langue';
import { useAncrageBas } from '../../hooks/useAncrageBas';
import { useFermetureNotifications } from '../../hooks/useFermetureNotifications';
import { useFermetureParRetour } from '../../hooks/useFermetureParRetour';
import { useFocusSurcouche } from '../../hooks/useFocusSurcouche';
import { useMasquageAutomatismes } from '../../hooks/useMasquageAutomatismes';

import EnTeteFil from './EnTeteFil';

/**
 * Le fil d'une conversation — #83, décisions #31 et #16.
 *
 * **Fil à plat, sans repli**, avec une bascule unique automatique / humain. Le
 * périmètre est de quatre sources contre onze au web (#31) : sept requêtes en
 * moins à l'ouverture, ce qui compte directement après le délai d'abandon court
 * décidé par #24.
 *
 * **Le retour ramène toujours à la liste** (#16) : on peut arriver ici par
 * notification (#20) sans avoir jamais vu la liste, et c'est quand même là qu'on
 * doit pouvoir aller.
 *
 * **Le nom de l'hôtel est porté par la conversation en scope groupe** (#25), pour
 * la même raison : elle ne peut pas hériter du contexte d'un en-tête de liste
 * qu'on n'a pas vu.
 */
export default function FilConversation() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentFacility } = useStateContext();

    // Ouvrir le fil referme les notifications de cette conversation (#77) : sur le
    // chemin déclaratif, la suppression d'affichage ne peut plus vivre dans le SW.
    // Avant tout `return` anticipé — les hooks n'en tolèrent aucun au-dessus d'eux.
    useFermetureNotifications(id);

    const [masque, setMasque] = useMasquageAutomatismes();
    const [emailOuvert, setEmailOuvert] = useState<Message | null>(null);

    const { elements, client, messages, reservations, isLoading, isError, recharger } = useFilConversation(id);
    const estGroupe = currentFacility?.is_group === true;

    // #85 : l'état se lit sur `client.folders`, déjà chargé — aucune requête de plus.
    // L'identifiant du dossier, lui, est chargé à l'ouverture du fil : le geste ne
    // doit pas attendre un aller-retour au moment du tap.
    const idDossierFerme = useIdDossierFerme(currentFacility?.id ?? null);
    const { fermer, rouvrir } = useFermerConversation(id ?? '');
    const fermee = estFermee(client);

    const basculerFermeture = () => {
        if (idDossierFerme === null) return;
        if (fermee) {
            rouvrir.mutate({ folderId: idDossierFerme });
            return;
        }
        // On revient à la liste : la conversation n'y est plus, et c'est la
        // confirmation. Aucun bandeau à faire disparaître ensuite.
        fermer.mutate({ folderId: idDossierFerme }, { onSuccess: () => navigate('/messaging') });
    };

    /*
     * Les séparateurs de jour sont calculés ICI, une fois par changement du fil,
     * et non à chaque rendu dans le JSX (audit 2026-09-23) : le fil se recharge
     * toutes les 30 s et chaque élément y payait deux `libelleJour`. Le jour
     * courant fait partie des dépendances : passé minuit, « Aujourd'hui » devient
     * « Hier » sans attendre un nouveau message.
     */
    const jourCourant = new Date().toDateString();
    const { visibles, lignes, masquesCount } = useMemo(() => {
        const automatismes = elements.filter(
            (e) => e.genre === 'message' && estAutomatique(e.message),
        ).length;

        const retenus = masque
            ? elements.filter((e) => e.genre !== 'message' || !estAutomatique(e.message))
            : elements;

        let jourPrecedent: string | null = null;
        const avecSeparateurs = retenus.map((element) => {
            const jour = libelleJour(element.date);
            const separateur = jour && jour !== jourPrecedent ? jour : null;
            jourPrecedent = jour;
            return { element, separateur };
        });

        return {
            visibles: retenus,
            lignes: avecSeparateurs,
            masquesCount: masque ? automatismes : 0,
        };
        // `jourCourant` n'est pas lu dans le calcul : il le PÉRIME à minuit.
    }, [elements, masque, jourCourant]);

    const { refFin, suivreProchainAjout } = useAncrageBas(
        visibles.at(-1)?.cle,
        !isLoading,
        id,
        visibles.length,
    );

    const refEmail = useFocusSurcouche(emailOuvert !== null);
    // Le retour referme l'e-mail et laisse sur le fil (D6) ; le retour suivant
    // garde son comportement d'avant.
    useFermetureParRetour(emailOuvert !== null, () => setEmailOuvert(null));

    const titre = client ? nomComplet(client) : t('messagerie.fil.conversation');

    // Un rechargement de fond en échec (poll 30 s) GARDE les messages en
    // react-query 3 : l'échec ne se dit que s'il n'y a rien à lire (relecture I1).
    // Le fil n'a de donnée que s'il a des messages : `messages` vaut `[]` avant
    // toute réponse.
    const echec = echecSansDonnee({ isError, data: messages.length > 0 ? messages : undefined });

    // Appeler en un tap — `/customer-basics` n'expose que `phone` brut : la
    // normalisation vit dans `telHref` (voir helpers/conversation.ts).
    const lienAppel = client ? telHref(client.phone) : null;

    /*
     * Remettre en non lu — #86.
     *
     * « Jamais d'échec silencieux » : un tap raté affiche l'alerte et REND LA MAIN
     * sur le fil, sans naviguer. Le succès, lui, referme le fil vers la liste
     * (#16) — c'est ce qui arrête le poll de 30 s et empêche `/get-messages` de
     * repasser la conversation en lu juste après qu'on l'ait remise à non lu (voir
     * `useMarquerNonLu`).
     */
    const nonLu = useMarquerNonLu(id ?? '');
    const [echecNonLu, setEchecNonLu] = useState(false);

    const marquerNonLu = () => {
        setEchecNonLu(false);
        nonLu.mutate(undefined, {
            onSuccess: () => navigate('/messaging'),
            onError: () => setEchecNonLu(true),
        });
    };

    return (
        <Screen
            pied={
                // Le pied est ce que le clavier recouvre : `Screen` lui réserve sa
                // hauteur via `useKeyboardInset` (#34), la barre reste visible.
                <Composeur
                    client={client}
                    messages={messages}
                    reservations={reservations}
                    onEnvoye={suivreProchainAjout}
                    messagesPrets={!isLoading && !echec}
                />
            }
            entete={
                // L'en-tête du fil est désormais le SEUL de l'écran (#85) : il
                // remplace celui de l'app plutôt que de s'empiler dessous.
                <EnTeteFil
                    titre={titre}
                    estGroupe={estGroupe}
                    nomEtablissement={client?.facility_name}
                    onRetour={() => navigate('/messaging')}
                    lienAppel={lienAppel}
                    idDossierFerme={idDossierFerme}
                    fermee={fermee}
                    fermetureEnCours={fermer.isLoading || rouvrir.isLoading}
                    onBasculerFermeture={basculerFermeture}
                    marquageNonLuEnCours={nonLu.isLoading}
                    onMarquerNonLu={marquerNonLu}
                    masque={masque}
                    onBasculerMasque={() => setMasque(!masque)}
                />
            }
        >
            {/* « Jamais d'échec silencieux » (#86) : le tap reste sur le fil, mais le
                dit — sans ça, quelqu'un croirait avoir remis la conversation en non
                lu alors que rien n'est parti. */}
            {echecNonLu && (
                <div role="alert" className="border-b border-border-color-light bg-white px-4 py-2">
                    <Text variante="petit" ton="danger">
                        {t('messagerie.fil.erreurNonLu')}
                    </Text>
                </div>
            )}

            {/*
             * Le témoin est la CONDITION de la bascule (#31 §4) : sans lui, masquer
             * recrée exactement le danger visé — répondre sans savoir ce que la
             * cliente a reçu. Il est permanent, pas un message d'un instant.
             */}
            {masquesCount > 0 && (
                <div className="sticky top-0 z-10 border-b border-border-color-light bg-main-color-light px-4 py-2">
                    <Text variante="petit" ton="marque">
                        {t('messagerie.fil.automatiquesMasques', { n: masquesCount })}
                    </Text>
                </div>
            )}

            <div className="px-3 py-3">
                {isLoading && (
                    <Text variante="petit" ton="attenue" className="p-6 text-center">
                        {t('commun.chargement')}
                    </Text>
                )}

                {echec && <EtatErreur onReessayer={recharger} texte={t('messagerie.fil.erreurChargement')} />}

                {!isLoading && !echec && visibles.length === 0 && (
                    <Text variante="petit" ton="attenue" className="p-6 text-center">
                        {t('messagerie.fil.vide')}
                    </Text>
                )}

                {lignes.map(({ element, separateur }) => (
                    <React.Fragment key={element.cle}>
                        {separateur && (
                            <Text as="p" variante="petit" ton="attenue" className="my-3 text-center">
                                {separateur}
                            </Text>
                        )}
                        {element.genre === 'message' ? (
                            <BulleMessage message={element.message} onOuvrirEmail={setEmailOuvert} />
                        ) : (
                            <CarteContexte element={element} />
                        )}
                    </React.Fragment>
                ))}

                {/* Sentinelle de fin de fil — toujours montée, l'ancrage s'y appuie (#A1). */}
                <div ref={refFin} aria-hidden="true" />
            </div>

            {/*
             * L'e-mail en PLEIN ÉCRAN, jamais en ligne (#16), dans un iframe isolé :
             * son HTML ne touche jamais le document de l'app (voir CorpsEmail).
             */}
            {emailOuvert && (
                <div
                    ref={refEmail}
                    role="dialog"
                    aria-modal="true"
                    aria-label={emailOuvert.object || t('canaux.email')}
                    tabIndex={-1}
                    className="tf-shell fixed inset-0 z-50 flex flex-col bg-white"
                >
                    <div className="flex shrink-0 items-center gap-2 border-b border-border-color-light px-3 py-2">
                        <IconButton libelle={t('messagerie.fil.fermerEmail')} onPress={() => setEmailOuvert(null)}>
                            <ArrowLeftIcon className="h-6 w-6 text-text-color" />
                        </IconButton>
                        <Text variante="sousTitre" as="h2" className="min-w-0 flex-1 truncate">
                            {emailOuvert.object || t('messagerie.fil.sansObjet')}
                        </Text>
                    </div>
                    <CorpsEmail html={emailOuvert.body ?? ''} titre={emailOuvert.object || t('canaux.email')} />
                </div>
            )}
        </Screen>
    );
}
